import React from "react";
import { Typography, Stack } from "@altimateai/lego";
import { ClickHouseQueryDetail } from "../../services/langfuseQueries";
import { StatusBadge } from "./StatusBadge";
import {
  truncateQuery,
  formatDuration,
  formatDate,
  getDisplayStatus,
  parseResultPreview,
} from "../utils/queryUtils";

interface QueryRowProps {
  query: ClickHouseQueryDetail;
  isExpanded: boolean;
  langfuseProjectId: string | null | undefined;
  onQueryClick: (queryId: string) => void;
}

export const QueryRow: React.FC<QueryRowProps> = ({
  query,
  isExpanded,
  langfuseProjectId,
  onQueryClick,
}) => {
  const traceUrl = langfuseProjectId
    ? `https://cloud.langfuse.com/project/${langfuseProjectId}/traces/${query.trace_id}`
    : null;

  return (
    <React.Fragment>
      <tr
        className="al-border-b al-cursor-pointer hover:al-bg-gray-50"
        onClick={() => onQueryClick(query.id)}
      >
        <td className="al-p-3 al-font-mono al-text-sm al-max-w-md">{truncateQuery(query.query)}</td>
        <td className="al-p-3 al-text-sm al-max-w-[200px] al-break-words">
          {query.tables.length > 0 ? query.tables.join(", ") : "-"}
        </td>
        <td className="al-p-3 al-text-sm">{query.tenant}</td>
        <td className="al-p-3 al-text-sm">{query.user_id || "-"}</td>
        <td className="al-p-3">
          <StatusBadge status={getDisplayStatus(query)} />
        </td>
        <td className="al-p-3 al-text-sm al-text-right">{formatDuration(query.duration_ms)}</td>
        <td className="al-p-3 al-text-sm al-text-gray-600">{formatDate(query.executed_at)}</td>
      </tr>
      {isExpanded && (
        <tr className="al-bg-gray-50">
          <td colSpan={7} className="al-p-4">
            <div className="al-space-y-3">
              <Stack direction="row" gap="md" align="center">
                <span>
                  <strong>User:</strong> {query.user_id || "-"}
                </span>
                {traceUrl && (
                  <a
                    href={traceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="al-text-blue-600 hover:al-underline"
                    onClick={e => e.stopPropagation()}
                  >
                    <Stack direction="row" align="center" gap="xs">
                      View trace in Langfuse
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </Stack>
                  </a>
                )}
              </Stack>
              <div>
                <Typography variant="caption" className="al-text-gray-500">
                  Full Query
                </Typography>
                <pre className="al-bg-gray-900 al-text-gray-100 al-p-3 al-rounded al-text-sm al-overflow-x-auto al-mt-1">
                  {query.query}
                </pre>
              </div>
              {query.error_message && (
                <div>
                  <Typography variant="caption" className="al-text-gray-500">
                    Error
                  </Typography>
                  <pre className="al-bg-red-50 al-text-red-800 al-p-3 al-rounded al-text-sm al-overflow-x-auto al-mt-1">
                    {query.error_message}
                  </pre>
                </div>
              )}
              {query.result_preview &&
                query.result_preview.length > 0 &&
                (() => {
                  const parsed = parseResultPreview(query.result_preview);
                  if (!parsed) return null;
                  return (
                    <div>
                      <Typography variant="caption" className="al-text-gray-500">
                        Result Preview
                      </Typography>
                      <div className="al-bg-gray-100 al-p-3 al-rounded al-overflow-x-auto al-mt-1">
                        <table className="al-w-full al-text-sm">
                          <thead>
                            <tr className="al-border-b al-border-gray-300">
                              {parsed.headers.map((header, idx) => (
                                <th
                                  key={idx}
                                  className="al-p-2 al-text-left al-font-medium al-text-gray-700 al-whitespace-nowrap"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {parsed.rows.map((row, rowIdx) => (
                              <tr
                                key={rowIdx}
                                className="al-border-b al-border-gray-200 last:al-border-0"
                              >
                                {row.map((value, colIdx) => (
                                  <td
                                    key={colIdx}
                                    className="al-p-2 al-text-gray-800 al-whitespace-nowrap"
                                  >
                                    {value}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};
